import { partStocksQuery } from "@common/api/part-stock"
import { useCreatePartMutation } from "@common/api/parts"
import type { PartStocksPublic } from "@common/client"
import { Button } from "@common/components/ui/button"
import { Form } from "@common/components/ui/form"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSharedForm } from "@common/hooks/use-shared-form"
import { handleError } from "@common/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { type QueryClient, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { AxiosError } from "axios"
import {
  DetailPane,
  DetailPanelActions,
  DetailPanelFields,
} from "@/components/layout/detail-panel"
import { PaneSections } from "@/components/page/pane"
import useCustomToast from "@/hooks/use-custom-toast"
import { PartControls, PartDetailsFields } from "./-components/part-panel"
import {
  type PartFormInput,
  partCreatePayload,
  partDefaults,
  partSchema,
} from "./-lib/part-form"
import { stockParams } from "./-lib/search"

export const Route = createFileRoute("/_layout/inventory/new")({
  staticData: { detailSidebar: true },
  component: NewPartPanel,
})

function hasNumber(page: PartStocksPublic, number: string): boolean {
  const wanted = number.toLowerCase()
  return page.data.some((stock) => stock.part?.number.toLowerCase() === wanted)
}

async function takenNumberMessage(
  queryClient: QueryClient,
  number: string,
): Promise<string | null> {
  const [live, archived] = await Promise.all(
    (["live", "archived"] as const).map((archive) =>
      queryClient.fetchQuery(
        partStocksQuery({ ...stockParams({ search: number }), archive }),
      ),
    ),
  )
  if (hasNumber(live, number)) {
    return "A part with this number already exists"
  }
  if (hasNumber(archived, number)) {
    return "An archived part has this number. Restore it from the Archived tab."
  }
  return null
}

function NewPartPanel(): React.ReactElement {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const mutation = useCreatePartMutation()
  const canCreate = useHasPermission("part:create")
  const form = useSharedForm<PartFormInput>({
    resolver: zodResolver(partSchema),
    defaultValues: partDefaults(null),
    criteriaMode: "all",
    mode: "onBlur",
  })
  const close = () => navigate({ to: "/inventory", search: true })

  const submit = async (values: PartFormInput) => {
    let taken: string | null
    try {
      taken = await takenNumberMessage(queryClient, values.number.trim())
    } catch (error) {
      if (!(error instanceof AxiosError)) throw error
      handleError(error, showToast)
      return
    }
    if (taken) {
      form.setError("number", { message: taken })
      return
    }
    mutation.mutate(
      { partDetails: partCreatePayload(values) },
      {
        onSuccess: (created) => {
          showToast("Created", `${created.number} added.`, "success")
          navigate({
            to: "/inventory/$partId",
            params: { partId: created.id },
            search: true,
          })
        },
        onError: (error) => handleError(error, showToast),
      },
    )
  }

  return (
    <DetailPane title="New part" onClose={close}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(submit)}
          className="flex h-full flex-col"
        >
          <DetailPanelFields>
            <PaneSections>
              <PartDetailsFields draft editable={canCreate} />
              <PartControls editable={canCreate} />
            </PaneSections>
          </DetailPanelFields>
          <DetailPanelActions
            dirty={form.formState.isDirty}
            pending={mutation.isPending || form.formState.isSubmitting}
            onReset={() => form.reset()}
            alwaysShowSave={canCreate}
          >
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
          </DetailPanelActions>
        </form>
      </Form>
    </DetailPane>
  )
}
